import { useState } from "react";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { activateUser } from "../services/activateUser";

export default function ActivationForm() {
  const navigate = useNavigate();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");

  const [formData, setFormData] = useState({
    OldEmail: "",
    OldPin: "",
    NewEmail: "",
    NewPin: "",
  });

  const handleSubmit = async () => {
    if (
      !formData.OldEmail ||
      !formData.OldPin ||
      !formData.NewEmail ||
      !formData.NewPin
    ) {
      await Swal.fire({
        title: "All fields must be filled!",
        text: "Please try again.",
        icon: "error",
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        allowOutsideClick: false,
      });
      return;
    }

    if (formData.NewPin !== confirmPassword) {
      await Swal.fire({
        title: "Pin does not match!",
        text: "Please try again.",
        icon: "error",
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        allowOutsideClick: false,
      });
      return;
    }

    try {
      const response = await activateUser(formData);

      if (response.Code == 0) {
        await Swal.fire({
          title: "Activate user successfully!",
          text: "Redirecting to login...",
          icon: "success",
          showConfirmButton: false,
          timer: 1200,
          timerProgressBar: true,
          allowOutsideClick: false,
        });

        navigate("/login");
      } else {
        await Swal.fire({
          title: "Activate user failed!",
          text: response.Message || "Please try again.",
          icon: "error",
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true,
          allowOutsideClick: false,
        });
      }
    } catch {
      await Swal.fire({
        title: "Activate user failed!",
        text: "Please try again.",
        icon: "error",
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        allowOutsideClick: false,
      });
    }
  };

  const inputClass =
    "w-full px-4 py-2.5 pl-10 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all duration-200";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 shadow-2xl border-2 border-gray-300 w-full max-w-md">

        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-blue-800 mb-1">
            Account Activation
          </h1>
          <p className="text-sm text-gray-500">
            Update your email and password
          </p>
        </div>

        <div className="space-y-3">

          <div className="relative">
            <Mail
              className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500"
              size={16}
            />
            <input
              type="text"
              placeholder="Old Email / Username"
              className={inputClass}
              value={formData.OldEmail}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  OldEmail: e.target.value,
                })
              }
            />
          </div>

          <div className="relative">
            <Mail
              className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500"
              size={16}
            />
            <input
              type="text"
              placeholder="New Email"
              className={inputClass}
              value={formData.NewEmail}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  NewEmail: e.target.value,
                })
              }
            />
          </div>

          <div className="relative">
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500"
              size={16}
            />
            <input
              type={showOldPassword ? "text" : "password"}
              placeholder="Old Password"
              className={`${inputClass} pr-10`}
              value={formData.OldPin}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  OldPin: e.target.value,
                })
              }
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowOldPassword(!showOldPassword)}
            >
              {showOldPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          <div className="relative">
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500"
              size={16}
            />
            <input
              type={showNewPassword ? "text" : "password"}
              placeholder="New Password"
              className={`${inputClass} pr-10`}
              value={formData.NewPin}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  NewPin: e.target.value,
                })
              }
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          <div className="relative">
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-500"
              size={16}
            />
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm New Password"
              className={`${inputClass} pr-10`}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          className="mt-5 w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-lg text-sm"
        >
          Activate Account
        </button>
      </div>
    </div>
  );
}